import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { formatCurrency, INVESTOR_FEE_OPTIONS } from './types';
import type { AutoFillRow } from './autoFillFromDocuments';

export interface AutoFillReviewDialogProps {
  open: boolean;
  /** e.g. "Final Purchase Reconciliation" or "Purchase Advice". */
  documentLabel: string;
  /** Overall classification confidence (0-1), if the doc was auto-classified. */
  classificationConfidence?: number;
  rows: AutoFillRow[];
  /** Apply the accepted rows (with any edits). Fills the form; does NOT save. */
  onApply: (acceptedRows: AutoFillRow[]) => void;
  /** Plain close (X / Esc / Cancel) — writes nothing, marks nothing, re-offers next load. */
  onCancel: () => void;
  /** "Skip this document" — writes nothing but marks the doc reviewed/dismissed so it won't re-pop. */
  onSkip: () => void;
}

interface EditState {
  checked: boolean;
  value: string;
  /** Investor-cost rows: the LO-chosen INVESTOR_FEE_OPTIONS id (defaults to the LLM's mapped type). */
  investorCostType?: string;
}

function confidenceBadge(confidence?: number): { label: string; className: string } | null {
  if (typeof confidence !== 'number') {
    return null;
  }
  const pct = `${Math.round(confidence * 100)}%`;
  if (confidence >= 0.95) {
    return { label: pct, className: 'bg-green-100 text-green-800 border-green-300' };
  }
  if (confidence >= 0.8) {
    return { label: pct, className: 'bg-amber-100 text-amber-800 border-amber-300' };
  }
  return { label: pct, className: 'bg-red-100 text-red-800 border-red-300' };
}

function formatValue(value: string | number | null, kind: AutoFillRow['kind']): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (kind === 'currency' && typeof value === 'number') {
    return formatCurrency(value);
  }
  if (kind === 'percent' && typeof value === 'number') {
    return `${value}%`;
  }
  return String(value);
}

export function AutoFillReviewDialog({
  open,
  documentLabel,
  classificationConfidence,
  rows,
  onApply,
  onCancel,
  onSkip
}: AutoFillReviewDialogProps) {
  const writableRows = useMemo(() => rows.filter((r) => !r.evidenceOnly), [rows]);
  const evidenceRows = useMemo(() => rows.filter((r) => r.evidenceOnly), [rows]);

  // Local edit state keyed by row.key. Reset whenever the row set changes (new doc).
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  useEffect(() => {
    const next: Record<string, EditState> = {};
    for (const row of writableRows) {
      next[row.key] = {
        checked: row.defaultChecked,
        value: String(row.extractedValue),
        ...(row.group === 'investor_cost' ? { investorCostType: row.investorCostType } : {})
      };
    }
    setEdits(next);
  }, [writableRows]);

  const handleApply = () => {
    const accepted: AutoFillRow[] = writableRows
      .filter((row) => edits[row.key]?.checked)
      .map((row) => {
        const raw = edits[row.key]?.value ?? String(row.extractedValue);
        const isNumeric = row.kind === 'currency' || row.kind === 'integer' || row.kind === 'percent';
        const value: string | number = isNumeric ? Number(raw) : raw;
        // Investor-cost rows carry the LO's (possibly re-categorized) type into the applied row.
        const investorCostType =
          row.group === 'investor_cost'
            ? edits[row.key]?.investorCostType ?? row.investorCostType
            : row.investorCostType;
        return { ...row, extractedValue: value, investorCostType };
      })
      // Drop numeric rows the user blanked into NaN.
      .filter((row) => !(typeof row.extractedValue === 'number' && Number.isNaN(row.extractedValue)));
    onApply(accepted);
  };

  const overallBadge = confidenceBadge(classificationConfidence);
  const groupLabels: Record<AutoFillRow['group'], string> = {
    wire: 'Wires',
    warehouse: 'Warehouse',
    investor_cost: 'Investor Costs',
    investor: 'Investor / Pricing'
  };

  const renderRow = (row: AutoFillRow) => {
    const edit = edits[row.key];
    const badge = confidenceBadge(row.confidence);
    return (
      <div key={row.key} className="flex items-start gap-3 py-2">
        <Checkbox
          id={`autofill-${row.key}`}
          checked={!!edit?.checked}
          onCheckedChange={(checked) =>
            setEdits((prev) => ({ ...prev, [row.key]: { ...prev[row.key], checked: checked === true } }))
          }
          className="mt-2"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <label htmlFor={`autofill-${row.key}`} className="text-sm font-medium text-gray-700">
              {row.label}
            </label>
            {badge && (
              <Badge variant="outline" className={`text-[10px] ${badge.className}`} title="Extraction confidence">
                {badge.label}
              </Badge>
            )}
            {row.encompassAuthoritative && (
              <span className="text-[10px] text-gray-400">already set by Encompass — check to overwrite</span>
            )}
            {!row.encompassAuthoritative && row.alreadySet && (
              <span className="text-[10px] text-gray-400">already set — check to overwrite</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Input
              type={row.kind === 'date' ? 'date' : 'text'}
              inputMode={row.kind === 'currency' || row.kind === 'integer' || row.kind === 'percent' ? 'decimal' : undefined}
              value={edit?.value ?? ''}
              onChange={(e) =>
                setEdits((prev) => ({ ...prev, [row.key]: { ...prev[row.key], value: e.target.value } }))
              }
              className="h-8 text-sm max-w-[220px]"
            />
            {row.group === 'investor_cost' && (
              <Select
                value={edit?.investorCostType ?? row.investorCostType ?? ''}
                onValueChange={(val) =>
                  setEdits((prev) => ({ ...prev, [row.key]: { ...prev[row.key], investorCostType: val } }))
                }
              >
                <SelectTrigger className="h-8 text-sm w-[170px]" aria-label="Investor cost type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {INVESTOR_FEE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {row.alreadySet && row.currentValue !== null && (
              <span className="text-xs text-gray-400">current: {formatValue(row.currentValue, row.kind)}</span>
            )}
            {row.key === 'targetActualPurchasePrice' && (
              <span className="text-[10px] text-gray-400">applied via purchase adjustment</span>
            )}
          </div>
          {row.docLabel && (
            <div className="mt-0.5 text-[10px] text-gray-400 truncate" title={row.docLabel}>
              from doc: “{row.docLabel}”
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review extracted data
            {overallBadge && (
              <Badge variant="outline" className={`text-[10px] ${overallBadge.className}`}>
                {overallBadge.label} match
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            From <span className="font-medium">{documentLabel}</span>. Review each value — applying fills the
            form; you still need to <span className="font-medium">Save</span> the draft to persist.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-3">
          {(['wire', 'warehouse', 'investor_cost', 'investor'] as const).map((group) => {
            const groupRows = writableRows.filter((r) => r.group === group);
            if (groupRows.length === 0) {
              return null;
            }
            return (
              <div key={group} className="mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  {groupLabels[group]}
                </div>
                {groupRows.map(renderRow)}
                <Separator className="my-2" />
              </div>
            );
          })}

          {evidenceRows.length > 0 && (
            <div className="mb-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Evidence (not applied)
              </div>
              {evidenceRows.map((row) => (
                <div key={row.key} className="flex items-center justify-between py-1 text-sm">
                  <span className="text-gray-600">{row.label}</span>
                  <span className="font-mono text-gray-800">{formatValue(row.extractedValue, row.kind)}</span>
                </div>
              ))}
            </div>
          )}

          {writableRows.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center">
              No new fields to fill — the document's values are already entered.
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="ghost" onClick={onSkip} className="text-gray-500">
            Skip this document
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={writableRows.length === 0}>
              Apply to form
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AutoFillReviewDialog;
